import { get, size } from "lodash";
import React, { JSX } from "react";

import { Info } from "lucide-react";

// The Content-Type each uploadable extension is sent as. The server signs the upload URL
// for exactly this type, so the upload must send the same value.
const UPLOAD_CONTENT_TYPES = new Map([
  ["pdf", "application/pdf"],
  ["jpg", "image/jpeg"],
  ["jpeg", "image/jpeg"],
  ["jfif", "image/jpeg"],
  ["png", "image/png"],
  ["heic", "image/heic"],
  ["tif", "image/tiff"],
  ["tiff", "image/tiff"],
]);

/**
 * The Content-Type to upload a file as, from its extension. Undefined for any other
 * extension; the server then refuses to sign the upload.
 */
export const getUploadContentType = (filePath: string): string | undefined =>
  UPLOAD_CONTENT_TYPES.get(filePath.split(".").pop()?.toLowerCase() ?? "");

/**
 * Returns a bullet point list of descriptions about each accepted doc type. Created based off what doc types are accepted by the client, as specified on the config
 * @param session
 * @returns
 */
export const getAcceptedDocumentDescriptions = (
  session: Record<string, any>,
  nextStep?: (step: string) => void
) => {
  const acceptedDocs = get(session, "config.documentation.acceptedDocs");

  const handleInfoClick = (type: string = "declaration-page") => {
    if (typeof nextStep === "function") {
      if (type === "id-card") {
        nextStep("id-card-sample");
      } else {
        nextStep("declaration-page-sample");
      }
    }
  };

  const helpLinkClassName =
    "inline-flex cursor-pointer items-center gap-1.5 self-start text-body-lg text-ink underline decoration-border underline-offset-4 hover:text-ink-secondary focus:text-ink-secondary";

  const decPageBullet: JSX.Element = (
    <li className="flex flex-col gap-1.5">
      <p className="text-body-lg text-ink-secondary text-pretty">
        <strong className="font-medium text-ink">Declarations page</strong>{" "}
        - a document with a list of your coverages and other detailed policy
        info
      </p>
      <span
        className={helpLinkClassName}
        onClick={() => handleInfoClick("declaration-page")}
        tabIndex={0}
        role="button"
        aria-label="Help me identify my declarations page"
      >
        <Info aria-hidden className="size-4" strokeWidth={1.75} />
        Help me identify my declarations page
      </span>
    </li>
  );

  const idCardBullet: JSX.Element = (
    <li className="flex flex-col gap-1.5">
      <p className="text-body-lg text-ink-secondary text-pretty">
        <strong className="font-medium text-ink">ID card</strong> - a
        small card, like a driver's license, shows high level policy info
      </p>
      <span
        className={helpLinkClassName}
        onClick={() => handleInfoClick("id-card")}
        tabIndex={0}
        role="button"
        aria-label="Help me identify my ID cards"
      >
        <Info aria-hidden className="size-4" strokeWidth={1.75} />
        Help me identify my ID cards
      </span>
    </li>
  );

  const heading: JSX.Element = (
    <h2 className="text-body-lg font-semibold text-ink">
      Accepted documents
    </h2>
  );

  // If client has not specified accepted docs, return default display text with both bullet points
  if (!acceptedDocs || !size(acceptedDocs)) {
    return (
      <div className="flex flex-col gap-3">
        {heading}
        <ul className="flex flex-col gap-3">
          {decPageBullet}
          {idCardBullet}
        </ul>
      </div>
    );
  }

  // Check which document types are accepted
  const hasDecPage = acceptedDocs.some(
    (doc: any) => doc.type === "declaration-page"
  );
  const hasIdCard = acceptedDocs.some((doc: any) => doc.type === "id-card");

  // Construct bullet points based on accepted document types
  return (
    <div className="flex flex-col gap-3">
      {heading}
      <ul className="flex flex-col gap-3">
        {hasDecPage && decPageBullet}
        {hasIdCard && idCardBullet}
      </ul>
    </div>
  );
};

/**
 * Forms the header on the manual document upload page based on the accepted document types
 * @param session
 * @returns
 */
export const getUploadHeader = (session: Record<string, any>): string => {
  let baseHeader = "Upload your";
  const acceptedDocs = get(session, "config.documentation.acceptedDocs");
  const defaultHeader = `${baseHeader} declarations page or insurance card`;

  // If client has not specified accepted docs, return default display text
  if (!acceptedDocs || !size(acceptedDocs)) {
    return defaultHeader;
  }

  const hasDecPage = acceptedDocs.some(
    (doc: any) => doc.type === "declaration-page"
  );
  const hasIdCard = acceptedDocs.some((doc: any) => doc.type === "id-card");

  // If client only supports dec page, return dec page header
  if (hasDecPage && !hasIdCard) {
    return `${baseHeader} declarations page`;
  }

  // If client only supports insurance card, return insurance card header
  if (!hasDecPage && hasIdCard) {
    return `${baseHeader} insurance card`;
  }
  // If client supports both, return generic header
  if (hasDecPage && hasIdCard) {
    return defaultHeader;
  }

  return defaultHeader;
};
